import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { 
  Paper, Box, Typography, FormControl, InputLabel, Select, MenuItem,
  Grid, Button, Accordion, AccordionSummary, AccordionDetails, Chip,
  Checkbox, ListItemText, ListSubheader, IconButton, Tooltip,
} from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import axios from 'axios';
import { alpha, useTheme } from '@mui/material/styles';

// Geo order matches UI: State → City (district) → Sub District (city) → RTO…
const HIERARCHY = ['year', 'state', 'district', 'city', 'rto', 'rtoCode', 'maker'];
const MONTH_OPTIONS = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
/** When only 2026 is selected, partial-year data — month dropdown is limited to Q1. */
const MONTH_OPTIONS_2026 = MONTH_OPTIONS.slice(0, 10);
const TYPE_OPTIONS = ['ev', 'ice'];

function normalizeFilterValue(key, val) {
  if (key === 'vehicleType') {
    if (Array.isArray(val)) return val.length ? val : ['ev'];
    if (val === undefined || val === null || val === '') return ['ev'];
    const parts = String(val).split(',').filter(Boolean);
    return parts.length ? parts : ['ev'];
  }
  if (Array.isArray(val)) return val;
  if (val === undefined || val === null || val === '') return [];
  return String(val).split(',').filter(Boolean);
}

const FILTER_KEYS = ['year', 'month', 'state', 'maker', 'rtoCode', 'rto', 'city', 'district', 'vehicleType'];

const FilterPanel = ({ 
  onFilterChange, 
  defaultFilters = {}, 
  showMakerFilter = true, 
  showTypeFilter = true,
  expanded = true,
  setExpanded = null
}) => {
  const [options, setOptions] = useState({
    years: [], states: [], districts: [], cities: [], rtos: [], rtoCodes: [], makers: [],
  });
  const [expandedInternal, setExpandedInternal] = useState(expanded);
  const isExpanded = setExpanded !== null ? expanded : expandedInternal;
  const setIsExpanded = setExpanded !== null ? setExpanded : setExpandedInternal;
  const theme = useTheme();
  const fetchIdRef = useRef(0);

  const [filters, setFilters] = useState(() => {
    const base = {
      year: [], month: [], state: [], maker: [],
      rtoCode: [], rto: [], city: [], district: [],
      vehicleType: ['ev'],
      ...defaultFilters,
    };
    const next = { ...base };
    FILTER_KEYS.forEach((k) => {
      if (k in next) next[k] = normalizeFilterValue(k, next[k]);
    });
    return next;
  });

  const monthOptionsList = useMemo(() => {
    const ys = filters.year || [];
    if (ys.length === 1 && String(ys[0]) === '2026') return MONTH_OPTIONS_2026;
    return MONTH_OPTIONS;
  }, [filters.year]);

  useEffect(() => {
    const ys = filters.year || [];
    if (ys.length !== 1 || String(ys[0]) !== '2026') return;
    setFilters((prev) => {
      const allowed = new Set(MONTH_OPTIONS_2026);
      const cur = prev.month || [];
      const nextMonth = cur.filter((m) => allowed.has(m));
      if (nextMonth.length === cur.length) return prev;
      return { ...prev, month: nextMonth };
    });
  }, [filters.year]);

  useEffect(() => {
    if (setExpanded !== null) setExpandedInternal(expanded);
  }, [expanded, setExpanded]);

  const fetchOptions = useCallback(async (currentFilters) => {
    const id = ++fetchIdRef.current;
    try {
      const params = {};
      const addParam = (key, val) => {
        if (!val) return;
        if (Array.isArray(val) && val.length > 0) params[key] = val.join(',');
        else if (typeof val === 'string' && val) params[key] = val;
      };
      addParam('year',     currentFilters.year);
      addParam('state',    currentFilters.state);
      addParam('district', currentFilters.district);
      addParam('city',     currentFilters.city);
      addParam('rto',      currentFilters.rto);
      addParam('rtoCode',  currentFilters.rtoCode);

      const res = await axios.get('/api/filter-options', { params });
      if (id !== fetchIdRef.current) return;

      setOptions({
        years:     res.data.years     || [],
        states:    res.data.states    || [],
        districts: res.data.districts || [],
        cities:    res.data.cities    || [],
        rtos:      res.data.rtos      || [],
        rtoCodes:  res.data.rtoCodes  || [],
        makers:    res.data.makers    || [],
      });
    } catch (err) {
      console.error('Error fetching filter options:', err);
    }
  }, []);

  useEffect(() => { fetchOptions(filters); }, []); // eslint-disable-line

  const clearChildren = (next, changedName) => {
    const idx = HIERARCHY.indexOf(changedName);
    if (idx >= 0) {
      for (let i = idx + 1; i < HIERARCHY.length; i++) {
        next[HIERARCHY[i]] = [];
      }
    }
  };

  const handleMultiChange = (e) => {
    const { name, value } = e.target;
    const selected = typeof value === 'string' ? value.split(',') : value;

    setFilters(prev => {
      const next = { ...prev, [name]: selected };
      clearChildren(next, name);
      fetchOptions(next);
      return next;
    });
  };

  const handleSelectAll = (name, optionsList) => {
    if (!optionsList || optionsList.length === 0) return;
    setFilters(prev => {
      const next = { ...prev, [name]: [...optionsList] };
      clearChildren(next, name);
      fetchOptions(next);
      return next;
    });
  };

  const handleDeselectAll = (name) => {
    setFilters(prev => {
      const next = { ...prev, [name]: [] };
      clearChildren(next, name);
      fetchOptions(next);
      return next;
    });
  };

  // Flatten filter values for the Apply callback — arrays become comma-separated strings
  const handleApplyFilters = () => {
    const flat = {};
    for (const [key, val] of Object.entries(filters)) {
      if (key === 'vehicleType' && Array.isArray(val) && val.length === 0) {
        flat[key] = 'ev';
      } else if (Array.isArray(val)) {
        flat[key] = val.join(',');
      } else {
        flat[key] = val;
      }
    }
    onFilterChange(flat);
    setIsExpanded(false);
  };

  const handleResetFilters = () => {
    const resetFilters = {
      year: [], month: [], state: [], maker: [],
      rtoCode: [], rto: [], city: [], district: [],
      vehicleType: ['ev'],
      ...defaultFilters,
    };
    FILTER_KEYS.forEach((k) => {
      if (k in resetFilters) resetFilters[k] = normalizeFilterValue(k, resetFilters[k]);
    });
    setFilters(resetFilters);
    const flat = {};
    for (const [key, val] of Object.entries(resetFilters)) {
      if (key === 'vehicleType' && Array.isArray(val) && val.length === 0) {
        flat[key] = 'ev';
      } else if (Array.isArray(val)) {
        flat[key] = val.join(',');
      } else {
        flat[key] = val;
      }
    }
    onFilterChange(flat);
    fetchOptions(resetFilters);
  };

  const compactSelect = {
    '& .MuiInputBase-root': { height: 36, fontSize: '0.85rem' },
    '& .MuiInputLabel-root': { fontSize: '0.85rem' },
  };

  const activeCount = Object.entries(filters).filter(
    ([k, v]) => {
      if (k === 'vehicleType') return false;
      if (Array.isArray(v)) return v.length > 0;
      return v && v !== 'all';
    }
  ).length;

  const renderMultiValue = (selected, formatOpt = (o) => o) => {
    if (!selected || selected.length === 0) return <em>All</em>;
    const labels = selected.map(formatOpt);
    if (labels.length <= 2) return labels.join(', ');
    return `${labels[0]}, +${labels.length - 1} more`;
  };

  const renderFilter = (name, label, optionsList, renderOpts = {}) => {
    const { formatOption = (o) => o } = renderOpts;
    const value = filters[name];
    const selected = Array.isArray(value) ? value : (value ? [value] : []);

    return (
      <FormControl fullWidth size="small" sx={compactSelect}>
        <InputLabel id={`${name}-label`}>{label}</InputLabel>
        <Select
          labelId={`${name}-label`}
          name={name}
          multiple
          value={selected}
          label={label}
          onChange={handleMultiChange}
          renderValue={(sel) => renderMultiValue(sel, formatOption)}
          MenuProps={{
            PaperProps: { style: { maxHeight: 340 } },
            autoFocus: false,
          }}
          sx={{ pr: 0.5 }}
        >
          <ListSubheader
            component="div"
            sx={{
              py: 0.35,
              px: 0.75,
              lineHeight: 1,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.25 }}>
              <Tooltip title="Select all" placement="top" arrow enterDelay={300}>
                <span>
                  <IconButton
                    size="small"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectAll(name, optionsList);
                    }}
                    disabled={!optionsList || optionsList.length === 0}
                    sx={{ p: 0.35, color: 'primary.main' }}
                    aria-label="Select all"
                  >
                    <SelectAllIcon sx={{ fontSize: '1.05rem' }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Deselect all" placement="top" arrow enterDelay={300}>
                <span>
                  <IconButton
                    size="small"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeselectAll(name);
                    }}
                    disabled={selected.length === 0}
                    sx={{ p: 0.35, color: 'text.secondary' }}
                    aria-label="Deselect all"
                  >
                    <ClearAllIcon sx={{ fontSize: '1.05rem' }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </ListSubheader>
          {optionsList.map(opt => (
            <MenuItem key={opt} value={opt} dense sx={{ minHeight: 30, py: 0.15 }}>
              <Checkbox
                size="small"
                sx={{ p: 0.35, mr: 0.5 }}
                checked={selected.indexOf(opt) > -1}
              />
              <ListItemText
                primary={formatOption(opt)}
                primaryTypographyProps={{ fontSize: '0.85rem', lineHeight: 1.15 }}
              />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  return (
    <Paper 
      elevation={0} 
      sx={{ mb: 0.25, mx: 2, borderRadius: '6px', overflow: 'hidden',
            border: '1px solid rgba(229, 231, 235, 1)', backgroundColor: 'background.paper' }}
    >
      <Accordion 
        expanded={isExpanded} 
        onChange={() => setIsExpanded(!isExpanded)}
        disableGutters elevation={0}
        sx={{ '&:before': { display: 'none' } }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ 
            backgroundColor: 'background.paper', color: 'text.primary',
            borderBottom: isExpanded ? `1px solid ${alpha(theme.palette.divider, 0.2)}` : 'none',
            '& .MuiAccordionSummary-expandIconWrapper': { color: theme.palette.text.secondary },
            py: 0.25, px: 1.5, minHeight: '36px !important'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FilterAltIcon sx={{ mr: 1, color: theme.palette.primary.main, fontSize: '1.2rem' }} />
            <Typography variant="subtitle2" fontWeight={600}>Filters</Typography>
            {activeCount > 0 && (
              <Chip label={activeCount} size="small" color="primary"
                    sx={{ ml: 1, height: 20, '& .MuiChip-label': { px: 1, fontSize: '0.75rem' } }} />
            )}
          </Box>
        </AccordionSummary>
        
        <AccordionDetails sx={{ px: 1, py: 0.5 }}>
          <Grid container spacing={1} columns={18}>
            {showTypeFilter && (
              <Grid item xs={8} sm={3} md={2}>
                {renderFilter('vehicleType', 'Type', TYPE_OPTIONS, {
                  formatOption: (v) => (v === 'ice' ? 'ICE' : 'EV'),
                })}
              </Grid>
            )}

            <Grid item xs={8} sm={3} md={2}>
              {renderFilter('year', 'Year', options.years)}
            </Grid>

            <Grid item xs={8} sm={3} md={2}>
              {renderFilter('month', 'Month', monthOptionsList)}
            </Grid>

            <Grid item xs={8} sm={3} md={2}>
              {renderFilter('state', 'State', options.states)}
            </Grid>

            <Grid item xs={8} sm={3} md={2}>
              {renderFilter('district', 'City', options.districts)}
            </Grid>

            <Grid item xs={8} sm={3} md={2}>
              {renderFilter('city', 'Sub District', options.cities)}
            </Grid>

            <Grid item xs={8} sm={3} md={2}>
              {renderFilter('rto', 'RTO', options.rtos)}
            </Grid>

            <Grid item xs={8} sm={3} md={2}>
              {renderFilter('rtoCode', 'RTO Code', options.rtoCodes)}
            </Grid>

            {showMakerFilter && (
              <Grid item xs={8} sm={3} md={2}>
                {renderFilter('maker', 'Maker', options.makers)}
              </Grid>
            )}
          </Grid>
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.75, gap: 0.75 }}>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleResetFilters} size="small" sx={{ fontSize: '0.75rem', py: 0.25 }}>
              Reset
            </Button>
            <Button variant="contained" disableElevation onClick={handleApplyFilters} size="small" sx={{ fontSize: '0.75rem', py: 0.25 }}>
              Apply
            </Button>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Paper>
  );
};

export default FilterPanel;